import { useState, useCallback, useEffect, ReactNode } from "react";
import NoDataFound from "components/NoDataFound";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RoutePath, BadgeStatus, DATE_FORMAT, RequestStatus, EntityType, IAppUserProfile} from "shared/constants";
import { Button, Table } from "@ds/ds-snam/react";
import { useDispatch } from "react-redux";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { getDrivers } from "net/contracts";
import SimpleTable, { SimpleTableType } from "components/SimpleTable";
import axios from "axios";
import moment from "moment";
import "views/contractView/styles/contractDataStyles.css";
import TableRighArrow from "components/TableRightArrow";
import { v4 as uuid } from "uuid";
import useRequestStatus from "hooks/useRequestStatus";
import { getBadgeStatus } from "views/contractView/shared/driverVehicleBadgeStatus";
import DsBadge from "components/DsBadge";
import DriverVehicleListTableLegend from "views/contractView/components/driverVehicleListTableLegend";
import AddEntityButton from "components/AddEntityButton";
import useAppUser from "hooks/useAppUser";
import { IDriverElement } from "dataModels/contract";
import { IParseDateFormatType, parseDateForDS } from "shared/utils/dateUtils";
import { ITableConfig, ITableRow } from "@ds/ds-snam/dist/models/tables";
import { getDSTableShaderElementsForFilterColumn,getDSTableSharedElements, getTableCellsHeaderSimple  } from "shared/utils/dsComponentUtils";

const DriversList = (): JSX.Element => {
    const { t } = useTranslation(['contract', 'app']);
    const appUserProfile:IAppUserProfile | undefined = useAppUser()?.userProfile;

    const [driverList, setDriverList] = useState<
        {
            [BadgeStatus.TO_CREDIT]: IDriverElement[],
            [BadgeStatus.CREDITED]: IDriverElement[]
        }
    >();
    const [driverListFiltered, setDriverListFiltered] = useState<
        {
            [BadgeStatus.TO_CREDIT]: IDriverElement[],
            [BadgeStatus.CREDITED]: IDriverElement[]
        }
    >();
    const [filtersAwaiting, setFiltersAwaiting] = useState<{ [key: string]: string }>({
            col1: "",
            col2: "",
        });
    const [filtersCredited, setFiltersCredited] = useState<{ [key: string]: string }>({
        col1: "",
        col2: ""
    });        

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const requestStatus = useRequestStatus();


    const loadDrivers = useCallback(() => {
        dispatch(requestPending());
        const toCreditReq = getDrivers(BadgeStatus.TO_CREDIT);
        const creditedReq = getDrivers(BadgeStatus.CREDITED);

        axios.all([toCreditReq, creditedReq])
            .then(axios.spread((...responses) => {
                //console.log(responses)
                setDriverList({
                    [BadgeStatus.TO_CREDIT]: responses[0],
                    [BadgeStatus.CREDITED]: responses[1]
                });
                setDriverListFiltered({
                    [BadgeStatus.TO_CREDIT]: responses[0],
                    [BadgeStatus.CREDITED]: responses[1]
                });
            }))
            .catch((error) => {
                console.log(error)
            })
            .finally(() => {
                dispatch(requestPristine())
            })
    }, [dispatch]);

    useEffect(() => {
        loadDrivers()
        return () => {
            dispatch(requestPristine());
        }
    }, [loadDrivers, dispatch]);

    const getColumns = (status: BadgeStatus) => {
        const translationSuffix: string = status === BadgeStatus.TO_CREDIT ? "notCredited" : "credited";
        return t("drivers.driverList.tableColumns." + translationSuffix, { ns: 'contract', returnObjects: true }) as string[];

    }

    const getRows = (tableType: BadgeStatus) => {
        if (!driverListFiltered)
            return [];
        const list: IDriverElement[] = tableType === BadgeStatus.TO_CREDIT ? driverListFiltered[BadgeStatus.TO_CREDIT] : driverListFiltered[BadgeStatus.CREDITED];

        let rows: ReactNode = list.map((driver: IDriverElement) => {

            return (
                <tr key={uuid()}>
                    <td>
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <DsBadge notificationStatus={getBadgeStatus(driver.status, tableType)} /> 
                            {driver.lastName} {driver.firstName} 
                        </span>
                    </td>
                    <td>{driver.transporterName}</td>
                    <td>{driver.date!==null?parseDateForDS(moment.unix(driver.date).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1):""}</td>
                    <td>
                        {/*tableType === BadgeStatus.TO_CREDIT ?
                            <Button
                                buttonType="button"
                                buttonUIVariant="secondary"
                                buttonSize="small"
                                buttonLabel={t('general.buttons.verifyElement', { ns: 'app', what: t('drivers.driverList.addButton') })}//"Verifica autista
                                //buttonIcon="icon-add"
                                isDisabled={false}
                                buttonAriaLabel="verify driver button"
                                onClickEvent={() => {
                                    navigate(driver.driverId);
                                }}
                            ></Button> :
                            <TableRighArrow onClick={() => {
                                navigate(driver.driverId);
                            }} />
                        */}
                        <TableRighArrow onClick={() => {
                                navigate(driver.driverId);
                            }} />
                    </td>
                </tr>
            );

        });

        return rows;

    }

    const getDriverListSearchColumnsAwaiting = (index: number) => {
        return index === 0 || index === 1 || index === 2; // Filtra solo le prime due colonne
    };
    
    const getDriverListSearchColumnsCredited = (index: number) => {
        return index === 0 || index === 1 || index === 2; // Filtra solo le prime due colonne
    };


    // Gestione dei cambiamenti dei filtri
    const handleFilterChangeAwaiting = (columnKey: string, value: string) => {
        setFiltersAwaiting((prevFilters) => ({
            ...prevFilters,
            [columnKey]: value,
        }));
    };
    // Gestione dei cambiamenti dei filtri
    const handleFilterChangeCredited = (columnKey: string, value: string) => {
        setFiltersCredited((prevFilters) => ({
            ...prevFilters,
            [columnKey]: value,
        }));
    };
    

    useEffect(() => {
        if (driverList) {
            setDriverListFiltered({
                [BadgeStatus.TO_CREDIT]: driverList[BadgeStatus.TO_CREDIT].filter(driver =>
                    (
                        driver.firstName.toLowerCase().includes(filtersAwaiting.col1.toLowerCase()) ||
                        driver.lastName.toLowerCase().includes(filtersAwaiting.col1.toLowerCase())
                    ) &&
                    driver.transporterName.toLowerCase().includes(filtersAwaiting.col2.toLowerCase())
                ),
                [BadgeStatus.CREDITED]: driverList[BadgeStatus.CREDITED].filter(driver =>
                    (
                        driver.firstName.toLowerCase().includes(filtersCredited.col1.toLowerCase()) ||
                        driver.lastName.toLowerCase().includes(filtersCredited.col1.toLowerCase())
                    ) &&
                    driver.transporterName.toLowerCase().includes(filtersCredited.col2.toLowerCase())
                ),
            });
        }
    }, [filtersAwaiting,filtersCredited]);

    const [sortOrderAwaiting, setSortOrderAwaiting] = useState<'asc' | 'desc' | null>(null);
    const [sortOrderCredited, setSortOrderCredited] = useState<'asc' | 'desc' | null>(null);
    
    const handleSortColumn2Awaiting = () => {
        if (driverListFiltered) {
            // Verifica se è la prima volta che si clicca (ordinamento non ancora impostato)
            const isFirstSort = sortOrderAwaiting === null;
    
            const sortedList = [...driverListFiltered[BadgeStatus.TO_CREDIT]].sort((a, b) => {
                const dateA = moment.unix(a.date);
                const dateB = moment.unix(b.date);
    
                // Se è la prima volta, ordina in decrescente, altrimenti alterna l'ordine
                if (isFirstSort) {
                    return dateB.diff(dateA); // Ordinamento decrescente per il primo clic
                } else {
                    return sortOrderAwaiting === 'asc' ? dateA.diff(dateB) : dateB.diff(dateA);
                }
            });
    
            setDriverListFiltered((prev) => ({
                [BadgeStatus.TO_CREDIT]: sortedList,
                [BadgeStatus.CREDITED]: prev?.[BadgeStatus.CREDITED] || []
            }));
    
            // Imposta l'ordine di ordinamento solo dopo il primo clic
            setSortOrderAwaiting((prev) => (isFirstSort ? 'asc' : prev === 'asc' ? 'desc' : 'asc'));
        }
    };
    
    const handleSortColumn2Credited = () => {
        if (driverListFiltered) {
            // Verifica se è la prima volta che si clicca (ordinamento non ancora impostato)
            const isFirstSort = sortOrderCredited === null;
    
            const sortedList = [...driverListFiltered[BadgeStatus.CREDITED]].sort((a, b) => {
                const dateA = moment.unix(a.date);
                const dateB = moment.unix(b.date);
    
                // Se è la prima volta, ordina in decrescente, altrimenti alterna l'ordine
                if (isFirstSort) {
                    return dateB.diff(dateA); // Ordinamento decrescente per il primo clic
                } else {
                    return sortOrderCredited === 'asc' ? dateA.diff(dateB) : dateB.diff(dateA);
                }
            });
    
            setDriverListFiltered((prev) => ({
                [BadgeStatus.TO_CREDIT]: prev?.[BadgeStatus.TO_CREDIT] || [],
                [BadgeStatus.CREDITED]: sortedList
            }));
    
            // Imposta l'ordine di ordinamento solo dopo il primo clic
            setSortOrderCredited((prev) => (isFirstSort ? 'asc' : prev === 'asc' ? 'desc' : 'asc'));
        }
    };
      const getTableRowsToCredit = (): ITableRow[] => {
            let myRetArr: ITableRow[] = [];
            if(driverListFiltered!==undefined){
              for (let i in driverListFiltered[BadgeStatus.TO_CREDIT]) {
                  let tempObj: ITableRow = {
                      tableCells: [
                          { 
                              type: "text-badge", 
                              text: driverListFiltered[BadgeStatus.TO_CREDIT][i].lastName +" " +driverListFiltered[BadgeStatus.TO_CREDIT][i].firstName,
                              badgeStatus: (driverListFiltered[BadgeStatus.TO_CREDIT][i].status === BadgeStatus.EXPIRED||driverListFiltered[BadgeStatus.TO_CREDIT][i].status === BadgeStatus.NOTAPPROVED||driverListFiltered[BadgeStatus.TO_CREDIT][i].status === BadgeStatus.NOT_CREDITED) ?
                              "error":
                              ( driverListFiltered[BadgeStatus.TO_CREDIT][i].status === BadgeStatus.CREDITED?
                                "confirm":
                                "warning"
                              )
                          },
                          { 
                              type: "text", 
                              text: driverListFiltered[BadgeStatus.TO_CREDIT][i].transporterName,
                          },
                          { 
                              type: "text", 
                              text: parseDateForDS(moment.unix(driverListFiltered[BadgeStatus.TO_CREDIT][i].date).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1),
                          }
                      ],
                      rightIcon: "icon-chevron-right",
                  };
                  myRetArr.push(tempObj);
              }
            }
            return myRetArr;
      };
      const getTableRowsCredited = (): ITableRow[] => {
        let myRetArr: ITableRow[] = [];
        if(driverListFiltered!==undefined){
          for (let i in driverListFiltered[BadgeStatus.CREDITED]) {
              let tempObj: ITableRow = {
                  tableCells: [
                      { 
                          type: "text-badge", 
                          text: driverListFiltered[BadgeStatus.CREDITED][i].lastName +" " +driverListFiltered[BadgeStatus.CREDITED][i].firstName,
                          badgeStatus: (driverListFiltered[BadgeStatus.CREDITED][i].status === BadgeStatus.EXPIRED||driverListFiltered[BadgeStatus.CREDITED][i].status === BadgeStatus.NOTAPPROVED||driverListFiltered[BadgeStatus.CREDITED][i].status === BadgeStatus.NOT_CREDITED) ?
                          "error":
                          ( driverListFiltered[BadgeStatus.CREDITED][i].status === BadgeStatus.APPROVED?
                            "confirm":
                            "warning"
                          )
                      },
                      { 
                          type: "text", 
                          text: driverListFiltered[BadgeStatus.CREDITED][i].transporterName,
                      },
                      { 
                          type: "text", 
                          text: parseDateForDS(moment.unix(driverListFiltered[BadgeStatus.CREDITED][i].date).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1),
                      }
                  ],
                  rightIcon: "icon-chevron-right",
              };
              myRetArr.push(tempObj);
          }
        }
        return myRetArr;
  };
      const myTableConfigToCredit: ITableConfig = {
              ...getDSTableSharedElements(280),
              tableHeader: {
                  
                  tableCellsHeader: [
                      {
                        ...getDSTableShaderElementsForFilterColumn(t("drivers.driverList.tableColumns.notCredited.0", { ns: 'contract' }) as string,"col1","34%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                        searchBoxValue:filtersAwaiting["col1"], //<---
                      },
                      {
                        ...getDSTableShaderElementsForFilterColumn(t("drivers.driverList.tableColumns.notCredited.1", { ns: 'contract' }) as string,"col2","33%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                          searchBoxValue:filtersAwaiting["col2"], //<---
                          
                      },
                      getTableCellsHeaderSimple(t("drivers.driverList.tableColumns.notCredited.2", { ns: 'contract' }) as string,"col3","33%","left",true),
                  ],
                  hasSearchBox: true,
              },
              tableRows: getTableRowsToCredit(),
          };
    const myTableConfigCredited: ITableConfig = {
            ...getDSTableSharedElements(280),
            tableHeader: {
                
                tableCellsHeader: [
                    {
                        ...getDSTableShaderElementsForFilterColumn(t("drivers.driverList.tableColumns.credited.0", { ns: 'contract' }) as string,"col1","34%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                        searchBoxValue:filtersCredited["col1"], //<---
                    },
                    {
                        ...getDSTableShaderElementsForFilterColumn(t("drivers.driverList.tableColumns.credited.1", { ns: 'contract' }) as string,"col2","33%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                        searchBoxValue:filtersCredited["col2"], //<---
                    },
                    getTableCellsHeaderSimple(t("drivers.driverList.tableColumns.credited.2", { ns: 'contract' }) as string,"col3","33%","left",true),
                ],
                hasSearchBox: true,
            },
            tableRows: getTableRowsCredited(),
        };

    return <>
        <p className="pageTitle" style={{ marginBottom: "10px" }}>
            {t('drivers.driverList.title')}
        </p>
        {/*<p className="pageSubTitle">
            {t('drivers.driverList.subTitle')}
        </p>*/}

        {appUserProfile?.addEntityPolicy.includes(EntityType.DRIVER)&&
            <div style={{ marginTop: "28px", marginBottom: "14px", display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
                <AddEntityButton entityType={EntityType.DRIVER} />
            </div>
        }

        {driverList && driverList[BadgeStatus.TO_CREDIT].length && requestStatus === RequestStatus.PRISTINE ?
            <><p className="driverAndVehicleListTableTitle">
                {/*Autisti in attesa di accreditamento*/}
                {t('drivers.driverList.tableTitle.notCredited')}
            </p>

                {/*<div className="listTableContainer">
                        <SimpleTable
                            id="simpleTable1"
                            tableType={SimpleTableType.LIST}
                            columns={getColumns(BadgeStatus.TO_CREDIT)}
                            rows={getRows(BadgeStatus.TO_CREDIT)} 
                            getSearchColumns={getDriverListSearchColumnsAwaiting}
                            columnInputHandlers={[
                                (value) => handleFilterChangeAwaiting("col1", value),
                                (value) => handleFilterChangeAwaiting("col2", value),
                                handleSortColumn2Awaiting
                            ]}
                            directInputColumns={[2]}
                        />
                </div>*/}
                <div className="listTableContainer">
                {
                    <Table 
                        tableConfig={myTableConfigToCredit} 
                        style={{}}
                        onInputSearchBarChange={(event:Event)=>{
                            //console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                        }}
                        onClickRightAction={(event:Event)=>{
                            //console.log(event)
                            const driverId = driverListFiltered?.[BadgeStatus.TO_CREDIT][(event as CustomEvent).detail.row].driverId;
                            if (driverId) {
                                navigate(driverId);
                            }
                        }}
                        //FILTERS
                        onSubmitSearchedText={(event:Event)=>{
                            const focusColtemp = (event as CustomEvent).detail.cells;
                            let focusCol = '';
                            switch (focusColtemp) {
                                case 0:
                                    focusCol = "col1";
                                    break;
                                case 1:
                                    focusCol = "col2";
                                    break;
                                default:
                                    break;
                            }
                            const focusValue = (event as CustomEvent).detail.searchedText;
                            handleFilterChangeAwaiting(focusCol, focusValue)
                        }}
                        onTableInputChange={(event:Event)=>{
                            //console.log(event)
                            const focusCol = (event as CustomEvent).detail.col;
                            const focusValue = (event as CustomEvent).detail.value;
                            //console.log(focusCol + " - " + focusValue)
                            //handleFilterChangeAwaiting(focusCol, focusValue)
                        }}
                        onColumnSorting={(event:Event)=>{
                            //console.log((event as CustomEvent).detail.columnID) 
                            switch((event as CustomEvent).detail.columnID){
                              case "col3":
                                handleSortColumn2Awaiting()
                                break;
                              default:  
                                break;
                            }
                        }}
                        onResetSearch={(event:Event)=>{
                            //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                            switch ((event as CustomEvent).detail.cells) {
                                case 0:
                                    handleFilterChangeAwaiting("col1", "");
                                    break;
                                case 1:
                                    handleFilterChangeAwaiting("col2", "");
                                    break;
                                default:
                                    break;
                            }
                        }}
                        onSearchBoxLeftButtonClick={()=>{
                            //console.log('cliccato');
                            /**
                                     * 
                                handleFilterChangeAwaiting("col1","");
                                handleFilterChangeAwaiting("col2","");
                                handleFilterChangeCredited("col1","");
                                handleFilterChangeCredited("col2","");
                                    */
                        }}
                    />
                }
                <DriverVehicleListTableLegend entityType={EntityType.DRIVER} tableType={BadgeStatus.NOT_CREDITED} />
                </div>

            </>:<></>}


        {driverList && driverList[BadgeStatus.CREDITED].length && requestStatus === RequestStatus.PRISTINE ?
            <div style={{marginTop:"36px"}}>
                <p className="driverAndVehicleListTableTitle">
                    {/*Autisti accreditati*/}
                    {t('drivers.driverList.tableTitle.credited')}
                </p>
                {/*<div className="listTableContainer">
                   
                        <SimpleTable
                            id="simpleTable2"
                            tableType={SimpleTableType.LIST}
                            columns={getColumns(BadgeStatus.CREDITED)}
                            rows={getRows(BadgeStatus.CREDITED)}
                            getSearchColumns={getDriverListSearchColumnsCredited}
                            columnInputHandlers={[
                                (value) => handleFilterChangeCredited("col1", value),
                                (value) => handleFilterChangeCredited("col2", value),
                                handleSortColumn2Credited
                            ]}
                            directInputColumns={[2]}
                        />
                    
                </div>*/}
                <div className="listTableContainer">
                {
                    <Table 
                        tableConfig={myTableConfigCredited} 
                        style={{}}
                        onInputSearchBarChange={(event:Event)=>{
                            //console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                        }}
                        onClickRightAction={(event:Event)=>{
                            //console.log(event)
                            const driverId = driverListFiltered?.[BadgeStatus.CREDITED][(event as CustomEvent).detail.row].driverId;
                            if (driverId) {
                                navigate(driverId);
                            }
                        }}
                        //FILTERS
                        onSubmitSearchedText={(event:Event)=>{
                            const focusColtemp = (event as CustomEvent).detail.cells;
                            let focusCol = '';
                            switch (focusColtemp) {
                                case 0:
                                    focusCol = "col1";
                                    break;
                                case 1:
                                    focusCol = "col2";
                                    break;
                                default:
                                    break;
                            }
                            const focusValue = (event as CustomEvent).detail.searchedText;
                            handleFilterChangeCredited(focusCol, focusValue)
                        }}
                        onTableInputChange={(event:Event)=>{
                            //console.log(event)
                            const focusCol = (event as CustomEvent).detail.col;
                            const focusValue = (event as CustomEvent).detail.value;
                            //console.log(focusCol + " - " + focusValue)
                            //handleFilterChangeCredited(focusCol, focusValue)
                        }}
                        onColumnSorting={(event:Event)=>{
                            //console.log((event as CustomEvent).detail.columnID) 
                            switch((event as CustomEvent).detail.columnID){
                              case "col3":
                                handleSortColumn2Credited()
                                break;
                              default:  
                                break;
                            }
                        }}
                        onResetSearch={(event:Event)=>{
                            //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                            switch ((event as CustomEvent).detail.cells) {
                                case 0:
                                    handleFilterChangeCredited("col1", "");
                                    break;
                                case 1:
                                    handleFilterChangeCredited("col2", "");
                                    break;
                                default:
                                    break;
                            }
                        }}
                        onSearchBoxLeftButtonClick={(event:Event)=>{
                            //console.log('cliccato2')
                                    /**
                                     * 
                                handleFilterChangeAwaiting("col1","");
                                handleFilterChangeAwaiting("col2","");
                                handleFilterChangeCredited("col1","");
                                handleFilterChangeCredited("col2","");
                                    */
                        }}
                    />
                }

                <DriverVehicleListTableLegend entityType={EntityType.DRIVER} tableType={BadgeStatus.CREDITED} />
                </div>
                
            </div>:<></>}

        {driverList && (driverList[BadgeStatus.TO_CREDIT].length === 0 && driverList[BadgeStatus.CREDITED].length === 0) && requestStatus === RequestStatus.PRISTINE &&
            <NoDataFound title={t('drivers.driverList.noData.title')} subTitle={t('drivers.driverList.noData.subTitle')} />
        }
    </>
}
export default DriversList;